// src/components/wizards/UploadSelectFilesStep.tsx
import React, { useCallback, useEffect, useState } from 'react'
import { Row, Col, Card, Button, ButtonProps } from 'react-bootstrap'
import { useProject } from './NewProjectContext'
import FileUploader from '../FileUploader'
import styled from 'styled-components'
import { FileText } from 'lucide-react'

const StepSection = styled.div`
  background: #212529;
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
`

interface StyledButtonProps extends ButtonProps {
  $active: boolean
}

const StyledButton = styled.button.attrs<StyledButtonProps>((props) => ({
  variant: 'outline-light',
}))<StyledButtonProps>`
  text-align: left;
  padding: 1rem;
  width: 100%;
  border: 1px solid ${(props) => (props.$active ? '#402563' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(props) => (props.$active ? 'rgba(64, 37, 99, 0.3)' : 'transparent')};

  &:hover {
    background: rgba(64, 37, 99, 0.2);
    border-color: #402563;
  }

  .choice-title {
    color: ${(props) => (props.$active ? '#fff' : '#f8f9fa')};
    font-weight: 500;
    margin-bottom: 0.25rem;
  }

  .choice-description {
    color: ${(props) => (props.$active ? 'rgba(255, 255, 255, 0.8)' : '#adb5bd')};
    font-size: 0.875rem;
  }
`

interface ChoiceButtonProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

const ChoiceButton = ({ active, onClick, children }: ChoiceButtonProps) => (
  <StyledButton $active={active} onClick={onClick}>
    {children}
  </StyledButton>
)

const UploadFilesStep: React.FC = () => {
  const {
    addFiles,
    fileMap,
    tiffFileMap,
    uploading = false,
    labelsFileInfo,
    setLabelsFile,
    labelsFormat,
    setLabelsFormat,
  } = useProject()

  const [hasLabels, setHasLabels] = useState<boolean>(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  useEffect(() => {
    setHasLabels(!!labelsFileInfo)
  }, [labelsFileInfo])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      addFiles(acceptedFiles)
    },
    [addFiles]
  )

  const handleLabelFileClick = () => {
    // Clear the input value so selecting the same file again triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.name.endsWith('.json')) {
      setLabelsFile(file)
    } else {
      setLabelsFile(undefined)
      event.target.value = ''
    }
  }

  const handleHasLabelsChange = (value: boolean) => {
    setHasLabels(value)
    if (!value) {
      setLabelsFile(undefined)
      setLabelsFormat('coco')
      // Also clear the hidden input so the same file can be re-selected later
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } else {
      setLabelsFormat('coco')
    }
  }

  const handleRemoveLabelsFile = () => {
    setLabelsFile(undefined)
    // Clear the hidden input so choosing the same file again will fire onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div>
      <Row className="g-3">
        <Col md={5}>
          <StepSection className="h-100">
            <h6 className="text-light mb-3">Do you have labels for these images?</h6>
            <Row className="g-3">
              <Col md={6}>
                <ChoiceButton
                  active={hasLabels === true}
                  onClick={() => handleHasLabelsChange(true)}
                >
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      <div className="choice-title">Yes, I have labels</div>
                      <div className="choice-description">
                        Upload images with a labels.json file
                      </div>
                    </div>
                  </div>
                </ChoiceButton>
              </Col>
              <Col md={6}>
                <ChoiceButton
                  active={hasLabels === false}
                  onClick={() => handleHasLabelsChange(false)}
                >
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      <div className="choice-title">No, just images</div>
                      <div className="choice-description">Upload images without labels</div>
                    </div>
                  </div>
                </ChoiceButton>
              </Col>
            </Row>

            {hasLabels === true && (
              <>
                <hr className="border-secondary my-4" />
                <h6 className="text-light mb-3">Labels Format</h6>
                <div className="d-flex gap-3 mb-3">
                  <Button
                    variant={labelsFormat === 'coco' ? 'light' : 'outline-light'}
                    onClick={() => setLabelsFormat('coco')}
                    className="flex-grow-1 text-white"
                    style={{
                      background: labelsFormat === 'coco' ? 'rgba(64, 37, 99, 0.3)' : 'transparent',
                      borderColor: labelsFormat === 'coco' ? '#402563' : 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    COCO
                  </Button>
                  <Button
                    variant={labelsFormat === 'ag2' ? 'light' : 'outline-light'}
                    onClick={() => setLabelsFormat('ag2')}
                    className="flex-grow-1 text-white"
                    style={{
                      background: labelsFormat === 'ag2' ? 'rgba(64, 37, 99, 0.3)' : 'transparent',
                      borderColor: labelsFormat === 'ag2' ? '#402563' : 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    AG2
                  </Button>
                </div>

                {labelsFileInfo ? (
                  <Card className="bg-dark" style={{ borderColor: '#402563' }}>
                    <Card.Body className="d-flex align-items-center justify-content-between py-2 px-3">
                      <div className="d-flex align-items-center gap-2">
                        <FileText size={16} style={{ color: '#402563' }} />
                        <div>
                          <div className="text-light small">{labelsFileInfo.name}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {labelsFormat === 'coco' ? 'COCO Format' : 'AG2 Format'}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveLabelsFile}
                        className="py-1 px-2"
                      >
                        Remove
                      </Button>
                    </Card.Body>
                  </Card>
                ) : (
                  <Button
                    variant="outline-light"
                    className="w-100 py-2"
                    onClick={handleLabelFileClick}
                    style={{
                      borderStyle: 'dashed',
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <FileText size={16} style={{ color: '#402563' }} className="me-2" />
                    <span>Select labels file</span>
                  </Button>
                )}
              </>
            )}
          </StepSection>
        </Col>

        <Col md={7}>
          <StepSection className="h-100">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="text-light mb-0">Images</h6>
              {(fileMap.size > 0 || tiffFileMap.size > 0) && (
                <span className="badge bg-info">
                  {fileMap.size + tiffFileMap.size} file
                  {fileMap.size + tiffFileMap.size !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <FileUploader
              onFileSelect={onDrop}
              uploading={uploading}
              numFiles={fileMap.size + tiffFileMap.size}
            />
          </StepSection>
        </Col>
      </Row>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  )
}

export default UploadFilesStep
